import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import { useDashboard } from '@/context/dashboardContext/DashboardContextProvider';
import Cookies from 'js-cookie';
import axiosInstance from '@/utils/axiosInstance'; // Import axiosInstance for API calls

const ClientURLs = () => {
  const {
    fetchClientResources,
    clientResources = [], // Ensure clientResources defaults to an empty array
    removeClientResource,
    addClientResource,
    editClientResource, // Ensure this is included
  } = useDashboard();

  const token = Cookies.get('token');
  const role = token ? JSON.parse(atob(token.split('.')[1])).role : null;
  const isSuperAdmin = role === 'SUPER ADMIN';

  const [newResource, setNewResource] = useState({
    id: 0,
    title: '',
    link: '',
    assignedRoles: [], // Update to handle multiple roles
    createdDate: new Date().toISOString(),
    updatedDate: new Date().toISOString(),
  });
  const [editingResource, setEditingResource] = useState(null);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [roles, setRoles] = useState([]); // Ensure roles is initialized as an empty array
  const [errors, setErrors] = useState({
    title: '',
    link: '',
    createdDate: '',
    roles: '',
  });

  useEffect(() => {
    fetchClientResources();
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/SmartProAPI/api/roles');
      console.log('Fetched roles:', response.data); // Debugging: Log fetched roles
      setRoles(response.data || []); // Fallback to an empty array if response.data is undefined
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRoles([]); // Fallback to an empty array on error
    }
  };

  const handleCreateResource = () => {
    const newErrors = {
      title: !newResource.title.trim() ? 'Title is required.' : '',
      link:
        !newResource.link.trim() || !isValidURL(newResource.link)
          ? 'A valid link is required.'
          : '',
      createdDate: !newResource.createdDate ? 'Created date is required.' : '',
      roles:
        newResource.assignedRoles.length === 0
          ? 'At least one role is required.'
          : '', // Validate roles
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    if (
      newResource.title &&
      newResource.link &&
      newResource.assignedRoles.length > 0
    ) {
      addClientResource(newResource);
      setNewResource({
        id: 0,
        title: '',
        link: '',
        assignedRoles: [], // Reset roles
        createdDate: '',
        updatedDate: '',
      });
      setErrors({ title: '', link: '', createdDate: '', roles: '' });
      setIsInputVisible(false);
    }
  };

  const handleEditResource = (resource) => {
    console.log('Editing resource:', resource); // Debugging: Log the resource being edited
    setEditingResource(resource);
    setNewResource({
      id: resource.id,
      title: resource.title,
      link: resource.link,
      assignedRoles: resource.assignedRoles || [], // Fallback to an empty array if undefined
      createdDate: resource.createdDate,
      updatedDate: new Date().toISOString(),
    });
    setIsInputVisible(true);
  };

  const handleUpdateResource = () => {
    const newErrors = {
      title: !newResource.title.trim() ? 'Title is required.' : '',
      link:
        !newResource.link.trim() || !isValidURL(newResource.link)
          ? 'A valid link is required.'
          : '',
      createdDate: !newResource.createdDate ? 'Created date is required.' : '',
      roles:
        newResource.assignedRoles.length === 0
          ? 'At least one role is required.'
          : '', // Validate roles
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    if (editingResource) {
      editClientResource(editingResource.id, newResource); // Pass the updated newResource to the API
      setEditingResource(null);
      setIsInputVisible(false);
      setErrors({ title: '', link: '', createdDate: '', roles: '' });
    }
  };

  const isValidURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <div className="relative m-5 bg-white shadow-lg rounded-xl border border-gray-200 h-80 overflow-y-auto ">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6 bg-white rounded-t-xl shadow-md sticky top-0 z-10 p-4">
        <h2 className="text-2xl font-bold text-gray-800">Client Resources</h2>
        {isSuperAdmin && (
          <button
            onClick={() => {
              setNewResource({
                id: 0,
                title: '',
                link: '',
                assignedRoles: [], // Reset roles
                createdDate: new Date().toISOString(),
                updatedDate: new Date().toISOString(),
              });
              setIsInputVisible(!isInputVisible);
              setErrors({ title: '', link: '', createdDate: '', roles: '' });
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-md transition-all p-2"
          >
            {!isInputVisible ? <FaPlus /> : <FaMinus />}
          </button>
        )}
      </div>

      {/* Input Form */}
      <div className="p-2">
        {' '}
        {isInputVisible && isSuperAdmin && (
          <div className="flex flex-col gap-4 mb-6">
            <div>
              <input
                type="text"
                placeholder="Enter title"
                className="border border-gray-300 p-3 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-full"
                value={newResource.title}
                onChange={(e) =>
                  setNewResource({ ...newResource, title: e.target.value })
                }
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                placeholder="Enter link"
                className="border border-gray-300 p-3 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-full"
                value={newResource.link}
                onChange={(e) =>
                  setNewResource({ ...newResource, link: e.target.value })
                }
              />
              {errors.link && (
                <p className="text-red-500 text-sm mt-1">{errors.link}</p>
              )}
            </div>
            <div>
              <p className="font-semibold text-gray-700 mb-2">Assign Roles:</p>
              <div className="flex flex-wrap gap-4">
                {roles.length === 0 && (
                  <p className="text-gray-500">No roles available.</p>
                )}
                {roles.map((role) => {
                  const isChecked = (newResource.assignedRoles || []).some(
                    (assignedRole) => assignedRole.id === role.id, // Match roles by id
                  );
                  console.log(
                    `Role: ${role.role}, Role ID: ${role.id}, Is Checked: ${isChecked}`,
                  ); // Debugging: Log role and its checked status
                  return (
                    <label
                      key={role.id}
                      className="flex items-center space-x-2"
                    >
                      <input
                        type="checkbox"
                        className="form-checkbox h-5 w-5 text-blue-600"
                        checked={isChecked}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const updatedRoles = isChecked
                            ? [...(newResource.assignedRoles || []), role] // Add role if checked
                            : (newResource.assignedRoles || []).filter(
                                (assignedRole) => assignedRole.id !== role.id,
                              ); // Remove role if unchecked
                          console.log('Updated assignedRoles:', updatedRoles); // Debugging: Log updated assignedRoles
                          setNewResource({
                            ...newResource,
                            assignedRoles: updatedRoles,
                          });
                        }}
                      />
                      <span className="text-gray-800">{role.role}</span>
                    </label>
                  );
                })}
              </div>
              {errors.roles && (
                <p className="text-red-500 text-sm mt-1">{errors.roles}</p>
              )}
            </div>
            <button
              className="w-full py-3 rounded-lg shadow-md text-white font-bold bg-green-500 hover:bg-green-600"
              onClick={
                editingResource ? handleUpdateResource : handleCreateResource
              }
            >
              {editingResource ? 'Update Resource' : 'Add Resource'}
            </button>
          </div>
        )}
        {/* Resources Display */}
        {clientResources.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-gray-500 italic text-center">
            <p>
              No client resources available. Add a new resource to get started!
            </p>
          </div>
        ) : (
          <>
            {!isInputVisible && (
              <div
                className="grid gap-6"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                }}
              >
                {(clientResources || []).map(
                  (
                    item, // Fallback to an empty array
                  ) => (
                    <div
                      key={item.id}
                      className="flex flex-col p-4 bg-gray-200/60 rounded-lg shadow-md hover:shadow-lg transition-all"
                    >
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-md text-gray-800 truncate">
                          {item.title}
                        </h3>
                        {isSuperAdmin && (
                          <div className="flex space-x-3">
                            <button
                              className="text-yellow-500 hover:text-yellow-600"
                              onClick={() => handleEditResource(item)}
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="text-red-500 hover:text-red-600"
                              onClick={() => removeClientResource(item.id)}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        )}
                      </div>
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 text-sm truncate mt-2"
                      >
                        {item.link}
                      </a>
                      <p className="text-gray-500 text-xs mt-1">
                        Assigned Roles:{' '}
                        {(item.assignedRoles || []) // Ensure assignedRoles is always an array
                          .map((role) => role.role)
                          .join(', ')}
                      </p>
                    </div>
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientURLs;
